import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import { sendPostMessage } from '@/clientOnlyUtils/messagingUtils';
import React, { useMemo } from 'react';

import Button from '@poseidon/components/Input2/Button';
import { Alerts, Arrows, General } from '@poseidon/components/StrokeIcons';
import { WaveSpinner, WaveSpinnerContainer } from '@poseidon/components/WaveSpinner';
import { cn } from '@poseidon/components/cn';
import { hasItems } from '@poseidon/shared-utils';

import type { PoseidonApiAuthRes } from '~/auth/auth.context';
import { Breadcrumb, Breadcrumbs } from '@poseidon/components/Breadcrumbs';
import { ButtonLink } from '@poseidon/components/Input2';
import { ContentDivider } from '@poseidon/components/ContentDivider';
import PageHeader from '@poseidon/components/PageHeader';
import { useTeamCreateMutation } from '@/hooks/trpc/useTeamMutation';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { AuthProblemCard } from '~/components/AuthProblemCard';
import { ImpersonationLoadFailed } from '~/components/ImpersonationLoadFailed';
import { ContentSection } from '@poseidon/components/ContentSection';
import { PaddedScrollable } from '@poseidon/components/Scrollable';
import { TeamsTable, type TeamRowTeam } from '~/components/TeamsTable';

export type PoseidonAuthData = NonNullable<PoseidonApiAuthRes['userTeam']> & {
  impersonation: { token: string; email: string } | null;
};
export type PoseidonSuperAdminAuthData = PoseidonAuthData & { isSuperAdmin: true };

const STUCK_AFTER_MS = 10_000;
const VERY_STUCK_AFTER_MS = 25_000;

const AuthLoader = ({ message }: { message: string }) => {
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Date.now() - start);
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const isStuck = elapsed >= STUCK_AFTER_MS;
  const isVeryStuck = elapsed >= VERY_STUCK_AFTER_MS;

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="text-zumthor-800 dark:bg-slate-900 flex h-screen w-screen flex-col items-center justify-center gap-6 px-6">
      <WaveSpinner />
      <div className="mb-1">{message}</div>

      <AuthProblemCard
        title="Taking longer than usual"
        className={cn('transition-opacity duration-300', isStuck ? 'opacity-100' : 'pointer-events-none opacity-0')}>
        {!isVeryStuck && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This usually fixes itself. If you&apos;re still stuck, try the steps below.
          </p>
        )}

        {isVeryStuck && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Still stuck? Try reloading the page first. If that doesn&apos;t work, sign out and sign back in.
          </p>
        )}

        <ol className="w-full list-decimal space-y-1 pl-6 text-left text-sm text-slate-700 dark:text-slate-200">
          <li>Click <span className="font-medium">Reload page</span> below.</li>
          <li>If it&apos;s still stuck, click <span className="font-medium">Sign out</span> and sign back in.</li>
          {isVeryStuck && (
            <li>
              Still not working? Try a different browser, or close and reopen this tab in a new private/incognito
              window.
            </li>
          )}
        </ol>

        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Button color="blue" icon={<Arrows.RefreshCcw05 />} onClick={handleReload}>
            Reload page
          </Button>
          <ButtonLink color="red" tone="outline" icon={<General.LogOut05 />} href="/logout">
            Sign out
          </ButtonLink>
        </div>

        {isVeryStuck && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            If none of these work, please contact support.
          </p>
        )}
      </AuthProblemCard>
    </div>
  );
};

export const AuthedPageWithTeam = (props: { children: (authData: PoseidonAuthData) => React.JSX.Element }) => {
  const [{ isPending, impersonation, ...authData }, { stopImpersonation }] = useAuthContext();
  const router = useRouter();
  // The impersonated person's account failed to load. /login can't fix that (you're still signed in
  // and the token is still stored), so offer the way out instead of redirecting there.
  const failedImpersonation = !isPending && authData.user == null && authData.isError ? impersonation : null;

  React.useEffect(() => {
    const routeChangeComplete = (url: string) => {
      sendPostMessage('URL_CHANGE', { url });
    };
    router.events.on('routeChangeComplete', routeChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null && failedImpersonation == null) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData.user, isPending, failedImpersonation, router]);

  if (isPending) {
    return <AuthLoader message="Authenticating" />;
  }

  if (failedImpersonation != null) {
    return <ImpersonationLoadFailed email={failedImpersonation.email} onStopImpersonating={stopImpersonation} />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (authData.userTeam != null) {
    return props.children({ ...authData.userTeam, impersonation });
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

type RedirectableProps = ComponentProps<'div'> & {
  redirect?: string;
};

export const SuperAdminAuthedPageWithTeam = (props: {
  children: (authData: PoseidonSuperAdminAuthData) => React.JSX.Element;
}) => {
  const [{ isPending, impersonation, ...authData }] = useAuthContext();
  const router = useRouter();

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && !('userTeam' in authData) && !('user' in authData)) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData, isPending, router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null) {
      const redirectPath = router.asPath.startsWith('/logout') ? undefined : router.asPath;
      void router.push({ pathname: '/login', query: redirectPath ? { redirect: redirectPath } : undefined });
    }
  }, [authData.user, isPending, router]);

  if (isPending) {
    return <AuthLoader message="Authenticating" />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (!authData.user.isSuperAdmin) {
    return <WaveSpinnerContainer message="Not Admin" />;
  }

  if (authData.userTeam != null) {
    return props.children({
      ...authData.userTeam,
      isSuperAdmin: authData.user.isSuperAdmin,
      impersonation,
    });
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

/**
 * forces the page to only show up if not logged in
 * redirects out to provided path defined by `redirect` prop or /leeds
 *
 * @param param0
 * @returns
 */
export const WithoutAuthPage = ({ children, redirect }: RedirectableProps) => {
  const [{ user, isPending: isLoading }] = useAuthContext();
  const router = useRouter();

  React.useEffect(() => {
    if (!isLoading && user != null) {
      // adding this execution to another tick to allow other
      // components to change redirect in query after login/signup
      setTimeout(() => {
        const _redirect = redirect || router.query.redirect;
        const redirectRoute =
          _redirect && typeof _redirect === 'string' && _redirect != '/logout' ? _redirect : '/leads';
        void router.push(redirectRoute);
      }, 0);
    }
  }, [user, isLoading, router, redirect]);

  if (isLoading) {
    return <>{children}</>;
  }

  if (user == null) {
    return <>{children}</>;
  } else {
    return null;
  }
};

const TeamPicker = () => {
  const allTeams = api.team.all.useQuery({});
  const teamCreateMutation = useTeamCreateMutation();
  const router = useRouter();

  // Map API response to component's expected interface
  const teams = useMemo<TeamRowTeam[]>(() => {
    if (!allTeams.data) return [];
    return allTeams.data.map((team) => ({
      teamId: team.teamId,
      teamPriceTier: team.teamPriceTier,
      teamName: team.teamName,
      teamLogo: team.teamLogo,
      userTeamStatus: team.userTeamStatus,
      totalUserCount: team.userCounts,
      nImages: team.nUserImages,
    }));
  }, [allTeams.data]);

  if (allTeams.isPending) {
    return <WaveSpinnerContainer message="Loading teams" />;
  }

  const onCreate = async () => {
    await teamCreateMutation.mutateAsync({
      makeDefault: true,
      data: { name: 'My Poseidon Team' },
    });
    void router.push('/teams/active');
  };

  const activeTeams = teams.filter((t) => t.userTeamStatus == 'active' || t.userTeamStatus == 'admin');

  const hasTeams = activeTeams.length > 0;
  return (
    <>
      <PageHeader icon={<General.Settings01 />} title={hasTeams ? 'Please select your team' : 'Please create a team'}>
        <ContentDivider>
          <Breadcrumbs showStartingChevron>
            <Breadcrumb>Teams</Breadcrumb>
          </Breadcrumbs>
        </ContentDivider>
      </PageHeader>

      <PaddedScrollable>
        <ContentSection>
          {hasItems(teams) && <TeamsTable teams={teams} isLoading={allTeams.isPending} />}
          {activeTeams && activeTeams.length <= 0 && <Button onClick={onCreate}>Create a default team</Button>}
        </ContentSection>
        <ContentSection>
          <ContentDivider icon={<Alerts.AlertTriangle />}>Other actions</ContentDivider>
          <div className="flex flex-row items-start gap-2">
            <ButtonLink text icon={<General.LogOut05 />} href="/logout" type="reset">
              Log out
            </ButtonLink>
          </div>
        </ContentSection>
      </PaddedScrollable>
    </>
  );
};
